import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Button, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Select, Textarea, useDisclosure, VStack } from '@chakra-ui/react'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'
import { format as formatDate } from 'date-fns'
import { approveProjectInstallerPO, deleteProjectInstallerPO } from 'api/projectInstallerPOApi'
import { PROJECT_INSTALLER_PO_CATEGORIES, ModalProjectInstallerPOFormType } from './ModalProjectInstallerPOForm'
import { ModalPasswordConfirmation } from 'components/common'

const initialForm = {
  signedDoc: null
}

export const ModalProjectInstallerPOApprove = ({ isOpen, onClose, onEdit, type = ModalProjectInstallerPOFormType.Add, po, reload }) => {
  const { createToast } = useAppContext()
  const [form, setForm] = useImmer(initialForm)
  const [isSaving, setIsSaving] = useState(false)
  const disclosureRemovePO = useDisclosure()

  useEffect(() => {
    setForm(initialForm)
  }, [type, po])

  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      await approveProjectInstallerPO(po?.id, { ...form })
      createToast('success', 'Success', 'Po approved.')
      setIsSaving(false)
      onClose()
      reload()
      setForm(initialForm)
    } catch (err) {
      setIsSaving(false)
      createToast('error', 'Error', err)
    }
  }

  const handleRemove = async (id) => {
    try {
      await deleteProjectInstallerPO(id)
      disclosureRemovePO.onClose()
      onClose()
      reload()
      createToast('success', 'Success', 'Po removed.')
    } catch {
      createToast('error', 'Error', 'Failed to remove the PO.')
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setForm(draft => {
        draft[e.target.name] = e.target.files[0]
      })
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSave}>
            <ModalHeader>Installer PO</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack alignItems="none" gap={4}>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Item Name</FormLabel>
                      <Select name="name" value={po?.name || ''} isDisabled={true} placeholder="Select">
                        {Object.keys(PROJECT_INSTALLER_PO_CATEGORIES).map(v =>
                          <option key={PROJECT_INSTALLER_PO_CATEGORIES[v]} value={PROJECT_INSTALLER_PO_CATEGORIES[v]}>{PROJECT_INSTALLER_PO_CATEGORIES[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
                <Heading size="sm">Vendor</Heading>
                <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Company</FormLabel>
                      <Input type="text" name="company" value={po?.company || ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Phone</FormLabel>
                      <Input type="text" name="phone" value={po?.phone || ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Contact</FormLabel>
                      <Input type="text" name="contact" value={po?.contact || ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Email</FormLabel>
                      <Input type="text" name="email" value={po?.email || ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Address</FormLabel>
                      <Input type="text" name="address" value={po?.address || ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>City</FormLabel>
                      <Input type="text" name="city" value={po?.city || ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>State</FormLabel>
                      <Input type="text" name="state" value={po?.state || ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Zip</FormLabel>
                      <Input type="text" name="zip" value={po?.zip || ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                </Grid>
                <Heading size="sm">Extra</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Price</FormLabel>
                      <NumberInput
                        onChange={v => setForm(draft => { draft.price = parseFloat(v) })}
                        value={po?.price}
                        min={0}
                        step={0.01}
                        isReadOnly={true}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>ETA</FormLabel>
                      <Input type="date" name="eta" value={po?.eta ? formatDate(new Date(po?.eta), 'yyyy-MM-dd') : ''} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                </Grid>
                <FormControl>
                  <FormLabel>Notes</FormLabel>
                  <Textarea name="note" value={po?.note || ''} isReadOnly={true} rows="10" />
                </FormControl>
              </VStack>
              <Flex justifyContent="center">
                <FormControl isRequired>
                  <FormLabel>Signed Doc</FormLabel>
                  <Input type="file" name="signedDoc" onChange={handleFileChange} />
                </FormControl>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Flex justifyContent="flex-end" gap={4}>
                <Button type="button" colorScheme="red" onClick={() => disclosureRemovePO.onOpen()}>
                  Delete
                </Button>
                <Button type="submit" colorScheme="blue" isLoading={isSaving}>
                  Approve
                </Button>
                <Button onClick={() => {
                  onClose()
                  onEdit(po)
                }}>Edit</Button>
              </Flex>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      {disclosureRemovePO.isOpen && (
        <ModalPasswordConfirmation
          heading="Are you sure to remove the PO?"
          btnConfirmLabel="Remove"
          onConfirm={() => handleRemove(po.id)}
          {...disclosureRemovePO}
        />
      )}
    </>
  )
}

ModalProjectInstallerPOApprove.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  type: PropTypes.oneOf(Object.values(ModalProjectInstallerPOFormType)),
  po: PropTypes.object,
  reload: PropTypes.func
}
